import type { WhatsNewSeenI, WhatsNewShownDaysI } from "./visibility";

/**
 * Where dismissals live: this browser's localStorage, one record per user
 * (so a shared computer or an Admin's impersonation never dismisses someone
 * else's card). Blocked storage reads as "nothing dismissed" and the write
 * reports false; the module then remembers the dismissal for this page load.
 */

export const WHATS_NEW_STORAGE_KEY_PREFIX = "glencoco:whats-new:";

export const getWhatsNewStorageKey = (userId: string) =>
  `${WHATS_NEW_STORAGE_KEY_PREFIX}${userId}`;

const isSeenRecord = (value: unknown): value is WhatsNewSeenI =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).every((day) => typeof day === "string");

export const readWhatsNewSeen = (userId: string): WhatsNewSeenI => {
  try {
    const raw = window.localStorage.getItem(getWhatsNewStorageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : {};

    return isSeenRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const writeWhatsNewSeen = (
  userId: string,
  seen: WhatsNewSeenI
): boolean => {
  try {
    window.localStorage.setItem(
      getWhatsNewStorageKey(userId),
      JSON.stringify(seen)
    );

    return true;
  } catch {
    return false;
  }
};

/** The ET days each card was shown on, per user (C16-D425). */
export const getWhatsNewDaysStorageKey = (userId: string) =>
  `${WHATS_NEW_STORAGE_KEY_PREFIX}days:${userId}`;

const isShownDaysRecord = (value: unknown): value is WhatsNewShownDaysI =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (days) =>
      Array.isArray(days) && days.every((day) => typeof day === "string")
  );

export const readWhatsNewShownDays = (userId: string): WhatsNewShownDaysI => {
  try {
    const raw = window.localStorage.getItem(getWhatsNewDaysStorageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : {};

    return isShownDaysRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const writeWhatsNewShownDays = (
  userId: string,
  shownDays: WhatsNewShownDaysI
): boolean => {
  try {
    window.localStorage.setItem(
      getWhatsNewDaysStorageKey(userId),
      JSON.stringify(shownDays)
    );

    return true;
  } catch {
    return false;
  }
};
